import React from 'react';
import { useSignal } from '@preact/signals-react';
import { toast } from 'react-toastify';
import SelectTextField from '../../../molecules/SelectTextField';
import CurrencyInput from '../../../molecules/CurrencyInput';
import TextEdit from '../../../molecules/TextEdit';

interface FiduciaryAlienation {
    name: string;
    value: number;
    description?: string;
    guarantee_type: 'AF' | 'PENHOR';
}

interface FiduciaryAlienationManagerProps {
    items: FiduciaryAlienation[];
    onAdd: (item: FiduciaryAlienation) => void;
    onRemove: (index: number) => void;
    disabled?: boolean;
}

const GUARANTEE_TYPES = [
    { value: 'Estoque', label: 'Estoque' },
    { value: 'Maquinário', label: 'Maquinário' },
    { value: 'Imóvel', label: 'Imóvel' },
    { value: 'Grãos', label: 'Grãos' }
];

const GUARANTEE_CATEGORY = [
    { value: 'AF', label: 'Alienação Fiduciária' },
    { value: 'PENHOR', label: 'Penhor' }
];

const FiduciaryAlienationManager: React.FC<FiduciaryAlienationManagerProps> = ({
    items,
    onAdd,
    onRemove,
    disabled = false
}) => {
    const newItem = useSignal<FiduciaryAlienation>({
        name: '',
        value: 0,
        description: '',
        guarantee_type: 'AF'
    });

    const handleAdd = () => {
        if (!newItem.value.guarantee_type) {
            toast.error("Selecione a categoria (AF ou Penhor)");
            return;
        }
        if (!newItem.value.name) {
            toast.error("Selecione o tipo de garantia");
            return;
        }
        if (newItem.value.value <= 0) {
            toast.error("Informe um valor maior que zero");
            return;
        }

        onAdd({
            name: newItem.value.name,
            value: newItem.value.value,
            description: newItem.value.description || undefined,
            guarantee_type: newItem.value.guarantee_type
        });

        newItem.value = { name: '', value: 0, description: '', guarantee_type: 'AF' };
    };

    return (
        <div>
            <h2 className="text-xl font-semibold mt-4 mb-4">Alienação Fiduciária/Penhor</h2>

            <div className="grid grid-cols-12 gap-4 p-4 border rounded bg-gray-50 dark:bg-gray-700 dark:border-gray-600">
                <div className="col-span-2">
                    <SelectTextField
                        id="guarantee_category"
                        label="Categoria"
                        value={newItem.value.guarantee_type}
                        items={GUARANTEE_CATEGORY}
                        onChange={(e) => newItem.value = { ...newItem.value, guarantee_type: e.target.value as 'AF' | 'PENHOR' }}
                        disabled={disabled}
                    />
                </div>
                <div className="col-span-3">
                    <SelectTextField
                        id="fiduciary_name"
                        label="Tipo de Garantia"
                        value={newItem.value.name}
                        items={GUARANTEE_TYPES}
                        onChange={(e) => newItem.value = { ...newItem.value, name: e.target.value }}
                        disabled={disabled}
                    />
                </div>
                <div className="col-span-2">
                    <CurrencyInput
                        id="fiduciary_value"
                        label="Valor"
                        prefix="R$"
                        fractionDigits={2}
                        value={newItem.value.value}
                        onChange={(value) => newItem.value = { ...newItem.value, value }}
                        disabled={disabled}
                    />
                </div>
                <div className="col-span-3">
                    <TextEdit
                        id="fiduciary_description"
                        label="Descrição"
                        value={newItem.value.description || ''}
                        onChange={(e) => newItem.value = { ...newItem.value, description: e.target.value }}
                        disabled={disabled}
                    />
                </div>
                <div className="col-span-2 flex items-end">
                    <button
                        onClick={handleAdd}
                        disabled={disabled}
                        className="btn-add mb-4 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        Incluir
                    </button>
                </div>
            </div>

            <div className="mt-4 overflow-x-auto">
                <table className="min-w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                        <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider border-b dark:border-gray-600">Categoria</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider border-b dark:border-gray-600">Tipo</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider border-b dark:border-gray-600">Valor</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider border-b dark:border-gray-600">Descrição</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider border-b dark:border-gray-600">Ações</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-600">
                        {items.length === 0 ? (
                            <tr>
                                <td colSpan={5} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                                    Nenhuma garantia adicionada
                                </td>
                            </tr>
                        ) : (
                            items.map((item, index) => (
                                <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                    <td className="px-4 py-2 whitespace-nowrap text-gray-900 dark:text-gray-100 font-medium">
                                        {item.guarantee_type === 'AF' ? 'Alienação Fiduciária' : 'Penhor'}
                                    </td>
                                    <td className="px-4 py-2 whitespace-nowrap text-gray-900 dark:text-gray-100 font-medium">{item.name}</td>
                                    <td className="px-4 py-2 whitespace-nowrap text-gray-900 dark:text-gray-100 font-medium">
                                        R$ {item.value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                    </td>
                                    <td className="px-4 py-2 text-gray-900 dark:text-gray-100">{item.description || '-'}</td>
                                    <td className="px-4 py-2 whitespace-nowrap">
                                        <button
                                            onClick={() => onRemove(index)}
                                            disabled={disabled}
                                            className="bg-red-500 text-white px-2 py-1 rounded text-xs hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            Remover
                                        </button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default FiduciaryAlienationManager;
